import { NewShipyardDialogComponent } from '@components/new-shipyard-dialog/new-shipyard-dialog.component';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ShipyardService } from '@service/shipyard.service';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { VesselService } from '@service/vessel.service';
import { NewVesselDialogComponent } from '@components/new-vessel-dialog/new-vessel-dialog.component';
import { AuthService } from '@service/auth.service';
import { Shipyard } from 'icreate/common/models/shipyard';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { AccessRequestStatus } from 'icreate/common/models/access-request';
import { UserPictureService } from '@service/user-picture.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '@service/homepage-notifications.service';
import { NotificationsSocketService } from '@service/homepage-notifications-socket.service';
import { LocalStorageService } from '@service/local-storage.service';
import { MessageDialogService } from '@service/message-dialog.service';
@Component({
    selector: 'app-recent-pages',
    templateUrl: './recent-pages.component.html',
    styleUrls: ['./recent-pages.component.css'],
    standalone: false,
})
export class RecentPagesComponent implements OnInit, OnDestroy {
    @ViewChild('vesselList') vesselListElem: ElementRef;
    @ViewChild('shipyardList') shipyardListElem: ElementRef;

    isLoading: boolean = false;
    displayGridView: boolean = true;

    vesselsEditors: { [key: string]: { name; image; userOid }[] } = {};
    userVessels: Vessel[] = [];
    filteredVessels: Vessel[] = [];

    selectedShipyardsName: string[] = [];
    showShipyardsList: boolean = false;
    shipyardList: string[] = [];

    selectedVesselsName: string[] = [];
    showVesselsList: boolean = false;
    vesselList: Vessel[] = [];

    notifications = [];
    private destroySubscriptionSubject$ = new Subject<void>();

    private shipyardsNames: string[];
    private vesselNames: { [key: string]: string[] };

    constructor(
        private shipyardService: ShipyardService,
        private vesselService: VesselService,
        private authService: AuthService,
        private dialog: MatDialog,
        private userPictureService: UserPictureService,
        private translateService: TranslateService,
        private notificationsService: NotificationsService,
        private notificationsSocketService: NotificationsSocketService,
        private localStorageService: LocalStorageService,
        private messageDialogService: MessageDialogService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.vesselListElem?.nativeElement.contains(event.target) && this.showVesselsList) {
            this.showVesselsList = false;
        }
        if (!this.shipyardListElem?.nativeElement.contains(event.target) && this.showShipyardsList) {
            this.showShipyardsList = false;
        }
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        this.shipyardService.setSelectedShipyard(null);
        this.displayGridView = this.localStorageService.isVesselAndShipyardGridView('homepage');
        const currentUserOid = this.authService.getCurrentUser().oid;
        const shipyards = await this.shipyardService.getList();
        const unfilteredNotifications = await this.notificationsService.getAll(currentUserOid);
        this.notifications = this.notificationsService.filterNotifications(unfilteredNotifications);

        await this.updateShipyardList(shipyards);

        this.shipyardService
            .listUpdatedSubject()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async () => {
                const shipyardList = this.shipyardService.getUpdatedList();
                await this.updateShipyardList(shipyardList);
            });

        this.notificationsSocketService.startSubscriptions();
        this.notificationsSocketService
            .newNotificationSubject()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((notification: any) => {
                const found = this.notifications.find(not => not.id == notification.id);
                if (!found) {
                    this.notifications.unshift(notification);
                }
            });

        this.notificationsSocketService
            .updateNotificationSubject()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(notification => {
                this.notifications = this.notifications.map(not => {
                    if (not.id == notification.id) {
                        return notification;
                    }
                    return not;
                });
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
        this.notificationsSocketService.destroySubscriptions();
    }

    async updateShipyardList(shipyards) {
        this.userVessels = [];
        this.vesselList = [];
        this.shipyardList = [];
        if (shipyards.length == 0) return;
        this.shipyardsNames = shipyards.map(shipyard => shipyard.name);
        const currentUserOid = this.authService.getCurrentUser().oid;
        this.shipyardList = shipyards.map(shipyard => shipyard.name);
        const vessels: any[] = shipyards.flatMap((shipyard: Shipyard) =>
            shipyard.vessels.map(vessel => ({
                ...vessel,
                shipyardName: shipyard.name,
            }))
        );
        this.vesselList = vessels;
        this.vesselNames = shipyards.reduce((acc, { id, vessels }) => {
            acc[id] = vessels.map(vessel => vessel.name);
            return acc;
        }, {});
        vessels.forEach(async (vessel: Vessel) => {
            await this.checkAccessRequests(vessel, currentUserOid);
        });
    }

    getShipyardValue() {
        if (this.selectedShipyardsName.length == 0)
            return this.translateService.instant('recent.projects-access.filter.shipyards.title');

        return this.selectedShipyardsName.join(', ');
    }

    getShipyardList() {
        return this.shipyardList;
    }

    selectShipyard(item) {
        if (this.selectedShipyardsName.includes(item)) {
            this.selectedShipyardsName = this.selectedShipyardsName.filter(shipyardName => shipyardName != item);
            if (this.selectedShipyardsName.length == 0) {
                this.selectedVesselsName = [];
            }
        } else {
            this.selectedShipyardsName.push(item);
        }
        this.filterVesselList();
    }

    getVesselValue() {
        if (this.selectedVesselsName.length == 0)
            return this.translateService.instant('recent.projects-access.filter.vessels.title');

        return this.selectedVesselsName.join(', ');
    }

    showVesselList() {
        if (this.selectedShipyardsName.length == 0) return;
        this.showShipyardsList = false;
        this.showVesselsList = !this.showVesselsList;
    }

    getVesselList() {
        if (this.selectedShipyardsName.length == 0) return;
        const vessels = this.vesselList.filter((vessel: any) =>
            this.selectedShipyardsName.includes(vessel.shipyardName)
        );
        const vesselNames = vessels.map(vessel => vessel.number + ' - ' + vessel.name);
        return [...vesselNames];
    }

    selectVessel(item) {
        if (this.isVesselOptionDisabled(item)) return;
        if (this.selectedVesselsName.includes(item)) {
            this.selectedVesselsName = this.selectedVesselsName.filter(vesselName => vesselName != item);
        } else {
            this.selectedVesselsName.push(item);
        }
        this.filterVesselList();
    }

    async createBuilder() {
        const dialogRef = this.dialog.open(NewShipyardDialogComponent, {
            width: '474px',
            data: { shipyardsNames: this.shipyardsNames },
        });
        const shipyardData = await lastValueFrom(dialogRef.afterClosed());
        if (shipyardData) {
            this.isLoading = true;
            const { name, iApiId } = shipyardData;
            try {
                await this.shipyardService.create(name, iApiId);
                this.isLoading = false;
                this.messageDialogService.openMessage('recent.shipyard.create.notification.title', {
                    name,
                });
            } catch (err) {
                console.error(err);
                this.isLoading = false;
            }
        }
    }

    async createVessel() {
        const shipyardList = await this.shipyardService.getList();
        const dialogRef = this.dialog.open(NewVesselDialogComponent, {
            width: '474px',
            data: { shipyardList: shipyardList },
        });
        const vesselData = await lastValueFrom(dialogRef.afterClosed());
        if (vesselData) {
            this.isLoading = true;
            try {
                const userName = this.authService.getCurrentUser().name;
                vesselData.userName = userName;
                await this.vesselService.create(vesselData);
                this.isLoading = false;
                this.messageDialogService.openMessage('recent.vessel.create.notification.title', {
                    number: vesselData.number,
                    name: vesselData.name,
                });
            } catch (err) {
                console.error(err);
                this.isLoading = false;
            }
        }
    }

    async checkAccessRequests(vessel: Vessel, currentUserOid: string) {
        if (!vessel.ownerOid) return;
        const editors: { userOid: string; userName: string }[] = [];
        const vesselIdsSet = new Set();
        if (vessel.ownerOid === currentUserOid) {
            editors.push({ userOid: vessel.ownerOid, userName: vessel.ownerName });
            this.vesselsEditors[vessel.id] = [];
            this.userVessels.push(vessel);
            vesselIdsSet.add(vessel.id);
        } else if (vessel.projectStatus === ProjectStatus.Editing) {
            const requests = vessel.VesselAccessRequests || [];
            if (requests.length > 0) {
                requests.forEach(accessRequest => {
                    if (
                        accessRequest.requestStatus === AccessRequestStatus.Editing &&
                        accessRequest.userOid === currentUserOid
                    ) {
                        this.userVessels.push(vessel);
                        this.vesselsEditors[vessel.id] = [];
                        editors.push({ userOid: vessel.ownerOid, userName: vessel.ownerName });
                        vesselIdsSet.add(accessRequest.vesselId);
                    }
                });

                requests.forEach(accessRequest => {
                    if (
                        vesselIdsSet.has(accessRequest.vesselId) &&
                        accessRequest.requestStatus === AccessRequestStatus.Editing
                    ) {
                        editors.push({
                            userOid: accessRequest.userOid,
                            userName: accessRequest.userName,
                        });
                    }
                });
            }
        }
        const editorsOid = new Set();

        for await (const editor of editors) {
            let { userOid, userName } = editor;
            if (editorsOid.has(userOid)) continue;
            editorsOid.add(userOid);
            const editorPicture = await this.userPictureService.getUserPicture(userOid, userName);
            this.vesselsEditors[vessel.id].push({
                image: editorPicture,
                name: userName,
                userOid,
            });
        }

        this.filterVesselList();
    }

    filterVesselList() {
        if (this.userVessels.length === 0) return;
        if (this.selectedShipyardsName.length == 0 && this.selectedVesselsName.length == 0) {
            this.filteredVessels = this.userVessels;
            return;
        }
        if (this.selectedVesselsName.length == 0) {
            this.filteredVessels = this.userVessels.filter((vessel: any) =>
                this.selectedShipyardsName.includes(vessel.shipyardName)
            );
        } else {
            this.filteredVessels = this.userVessels.filter(
                (vessel: any) =>
                    this.selectedShipyardsName.includes(vessel.shipyardName) &&
                    this.selectedVesselsName.includes(vessel.number + ' - ' + vessel.name)
            );
        }
    }

    shipyardIsSelected(name) {
        return this.selectedShipyardsName.includes(name);
    }

    vesselIsSelected(name) {
        return this.selectedVesselsName.includes(name);
    }

    clearFilters() {
        this.selectedShipyardsName = [];
        this.selectedVesselsName = [];
        this.filterVesselList();
    }

    updateViewOption(shouldDisplayGrid) {
        this.displayGridView = shouldDisplayGrid;
        this.localStorageService.setShipyardViewMode('homepage', this.displayGridView);
    }

    isVesselOptionDisabled(vessel: string) {
        if (!this.userVessels || this.userVessels.length == 0) return true;
        return !this.userVessels.find(ves => ves.number + ' - ' + ves.name == vessel);
    }
}
